import { Injectable, Injector, InjectionToken } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { LogContext } from '../models';
import { environment } from 'src/environments/environment';
import { METADATA } from '../constants';

/**
 * Interface for asset metadata from DynamoDB
 */
interface AssetMetadata {
  partnerName?: string;
  partnerId?: string;
  institutionName?: string;
  institutionId?: string;
  externalId?: string;
  product?: string;
}

/**
 * Injection token for CcmcApiService to avoid circular dependencies
 */
const CCMC_API_SERVICE = new InjectionToken<any>('CcmcApiService');

/**
 * ContextService manages dynamic runtime context and metadata enrichment
 * for log records. Provides session management, environment detection,
 * and reactive context updates.
 * 
 */
@Injectable({
  providedIn: 'root'
})
export class ContextService {
  private context: LogContext;
  private contextSubject: BehaviorSubject<LogContext>;
  
  /**
   * Observable stream of context updates for reactive components
   */
  public readonly contextUpdates$: Observable<LogContext>;

  // Lazy injection to avoid circular dependencies
  private injector: Injector;

  constructor(injector: Injector) {
    this.injector = injector;
    
    // Initialize base context with session and environment
    this.context = this.initializeBaseContext();
    this.contextSubject = new BehaviorSubject<LogContext>(this.context);
    this.contextUpdates$ = this.contextSubject.asObservable();
  }

  /**
   * Initialize base context with default values
   */
  private initializeBaseContext(): LogContext {
    const sessionId = this.generateSessionId();
    const environment = this.detectEnvironment();
    
    return {
      timestamp: new Date().toISOString(),
      sessionId,
      environment,
      service: METADATA.getServiceName(environment),
      thread: 'main',
      userAgent: this.getBrowserUserAgent(),
      url: this.getCurrentUrl()
    };
  }

  /**
   * Generate unique session identifier using UUID v4
   */
  private generateSessionId(): string {
    return uuidv4();
  }

  /**
   * Generate unique correlation identifier for request tracing
   */
  generateCorrelationId(): string {
    return uuidv4();
  }

  /**
   * Detect current environment based on Angular environment configuration
   */
  private detectEnvironment(): string {
    // First check Angular environment flags
    if (environment.production) {
      return 'prod';
    }
    
    if (environment.test) {
      return 'test';
    }
    
    if (environment.dev) {
      return 'dev';
    }

    // Fallback to hostname detection if environment flags are not set
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname.toLowerCase();
      
      if (hostname.includes('localhost') || 
          hostname.includes('127.0.0.1') || 
          hostname.includes('dev')) {
        return 'dev';
      }
      
      if (hostname.includes('test') || 
          hostname.includes('staging') || 
          hostname.includes('stage')) {
        return 'test';
      }
      
      return 'prod';
    }
    
    return 'unknown';
  }

  /**
   * Get browser user agent string safely
   */
  private getBrowserUserAgent(): string | undefined {
    if (typeof navigator !== 'undefined') {
      return navigator.userAgent;
    }
    return undefined;
  }

  /**
   * Get current URL safely
   */
  private getCurrentUrl(): string | undefined {
    if (typeof window !== 'undefined') {
      return window.location.href;
    }
    return undefined;
  }

  /**
   * Get current context with real-time timestamp updates
   */
  getContext(): LogContext {
    return {
      ...this.context,
      timestamp: new Date().toISOString(),
      url: this.getCurrentUrl() // Update URL in case of navigation
    };
  }

  /**
   * Update component context for tracking current component and action
   */
  updateComponentContext(component: string, action?: string): void {
    this.context = {
      ...this.context,
      component,
      action,
      timestamp: new Date().toISOString()
    };
    
    this.emitContextUpdate();
  }

  /**
   * Clear component context
   */
  clearComponentContext(): void {
    this.context = {
      ...this.context,
      component: undefined,
      action: undefined,
      timestamp: new Date().toISOString()
    };
    
    this.emitContextUpdate();
  }

  /**
   * Enrich context after user login with asset metadata
   */
  async enrichContextAfterLogin(assetId: string): Promise<void> {
    setTimeout(async () => {
      try {
        // Fetch asset metadata 
        const assetMetadata = await this.fetchAssetMetadata(assetId);
        
        this.context = {
          ...this.context,
          assetId,
          partner: METADATA.PARTNER_NAME,
          partnerId: assetMetadata?.partnerId,
          institution: assetMetadata?.institutionName,
          product: METADATA.PRODUCT_NAME,
          externalId: assetMetadata?.externalId,
          timestamp: new Date().toISOString()
        };
        
        this.emitContextUpdate();
      } catch (error) {
        this.context = {
          ...this.context,
          assetId,
          product: METADATA.PRODUCT_NAME,
          partner: METADATA.PARTNER_NAME,
          partnerId: undefined, // No asset metadata available
          externalId: undefined, // No asset metadata available
          timestamp: new Date().toISOString()
        };
        
        this.emitContextUpdate();
      }
    }, 0);
  }

  /**
   * Update user context with authentication information
   */
  updateUserContext(userContext: Partial<LogContext>): void {
    this.context = {
      ...this.context,
      ...userContext,
      timestamp: new Date().toISOString()
    };
    
    this.emitContextUpdate();
  }

  /**
   * Clear user-specific context while preserving session data
   */
  clearUserContext(): void {
    const { sessionId, environment, service, thread, userAgent } = this.context;
    
    this.context = {
      timestamp: new Date().toISOString(),
      sessionId,
      environment,
      service,
      thread,
      userAgent,
      url: this.getCurrentUrl()
    };
    
    this.emitContextUpdate();
  }

  /**
   * Fetch asset metadata from DynamoDB with error handling and retry logic
   */
  private async fetchAssetMetadata(assetId: string): Promise<AssetMetadata | null> {
    // Wrap in Promise to ensure truly async execution
    return new Promise((resolve) => {
      // Use setTimeout to push to next event loop tick
      setTimeout(async () => {
        try {
          // Lazy initialize CcmcApiService to avoid circular dependencies
          let ccmcApiService: any = null;
          
          // Use optional injection to avoid deprecation warnings
          try {
            ccmcApiService = this.injector.get(CCMC_API_SERVICE, null);
          } catch (e) {
            // Service not available - this is expected and handled gracefully
            ccmcApiService = null;
          }

          if (!ccmcApiService) {
            console.warn('CcmcApiService not available, skipping asset metadata fetch');
            resolve(null);
            return;
          }

          if (!ccmcApiService.getDynamoDBItem) {
            console.warn('getDynamoDBItem method not available');
            resolve(null);
            return;
          }

          // Fetch asset data from DynamoDB using existing service
          const response = await ccmcApiService.getDynamoDBItem('assets', assetId, 'assetId')
            .pipe(
              timeout(2000), // Reduced to 2 second timeout to prevent blocking
              catchError((error: any) => {
                // Graceful fallback - return null on error
                console.warn('Failed to fetch asset metadata:', error);
                return of(null);
              })
            )
            .toPromise();

          if (!response) {
            resolve(null);
            return;
          }

          // Extract relevant metadata from DynamoDB response
          const metadata: AssetMetadata = {
            partnerName: response.coreCompany || response.partner || response.partnerName,
            partnerId: response.partnerId,
            institutionName: response.FI || response.institutionName,
            institutionId: response.institutionId || assetId,
            externalId: response.externalId,
            product: response.product
          };

          resolve(metadata);
        } catch (error) {
          // Graceful fallback when metadata is unavailable
          console.warn('Asset metadata fetch failed:', error);
          resolve(null);
        }
      }, 0);
    });
  }

  /**
   * Emit context update to reactive subscribers
   */
  private emitContextUpdate(): void {
    this.contextSubject.next({ ...this.context });
  }
}